import { ChipInput } from '@/components/ChipInput';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { useT } from '@/i18n';
import type { CategorySchedule } from '@restaurant/api-contracts';
import { useState } from 'react';
import { Block, BlockTitle, Button, Chip, Link as F7Link, List, ListItem } from 'framework7-react';

type ScheduleWindow = { days: number[]; startTime: string; endTime: string };

/**
 * Day-of-week + time-window availability editor, shared by the category and
 * product forms. Self-managing: it seeds its state from `value` ON MOUNT, so give
 * it a `key` that changes when the edited entity (or sheet open-state) changes to
 * re-seed. It calls `onChange` with the built schedule - or `null` (= always
 * available) - on every edit.
 */
export default function ScheduleEditor({
  value,
  onChange,
}: {
  value: CategorySchedule | null | undefined;
  onChange: (next: CategorySchedule | null) => void;
}) {
  const t = useT();
  const init = (value?.windows ?? []) as ScheduleWindow[];
  const [scheduled, setScheduled] = useState(init.length > 0);
  const [windows, setWindows] = useState<ScheduleWindow[]>(
    init.map((w) => ({ days: [...w.days], startTime: w.startTime, endTime: w.endTime })),
  );

  const emit = (sch: boolean, ws: ScheduleWindow[]) => {
    if (!sch) return onChange(null);
    const valid = ws.filter((w) => w.days.length > 0 && w.startTime && w.endTime);
    onChange(valid.length > 0 ? { windows: valid } : null);
  };

  const applyWindows = (ws: ScheduleWindow[]) => {
    setWindows(ws);
    emit(scheduled, ws);
  };

  const toggleScheduled = (v: boolean) => {
    let ws = windows;
    if (v && ws.length === 0) {
      ws = [{ days: [1, 2, 3, 4, 5, 6, 0], startTime: '06:00', endTime: '11:00' }];
      setWindows(ws);
    }
    setScheduled(v);
    emit(v, ws);
  };

  const toggleDay = (wi: number, day: number) =>
    applyWindows(
      windows.map((w, i) =>
        i === wi
          ? {
              ...w,
              days: w.days.includes(day)
                ? w.days.filter((d) => d !== day)
                : [...w.days, day].sort(),
            }
          : w,
      ),
    );

  const setTime = (wi: number, key: 'startTime' | 'endTime', val: string) =>
    applyWindows(windows.map((x, i) => (i === wi ? { ...x, [key]: val } : x)));

  const removeWindow = (wi: number) => applyWindows(windows.filter((_, i) => i !== wi));
  const addWindow = () =>
    applyWindows([
      ...windows,
      { days: [1, 2, 3, 4, 5, 6, 0], startTime: '17:00', endTime: '22:00' },
    ]);

  return (
    <>
      <BlockTitle>{t.menu.availabilitySchedule}</BlockTitle>
      <List strongIos insetIos dividersIos>
        <ListItem title={t.menu.restrictToCertainTimes}>
          <ToggleSwitch slot="after" checked={scheduled} onChange={toggleScheduled} />
        </ListItem>
      </List>
      {scheduled &&
        windows.map((w, wi) => (
          <div key={wi}>
            <BlockTitle>
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ flex: 1 }}>{t.menu.window(wi + 1)}</span>
                <F7Link
                  iconF7="trash"
                  style={{ color: 'var(--f7-color-red)' }}
                  onClick={() => removeWindow(wi)}
                />
              </span>
            </BlockTitle>
            <Block strongIos insetIos>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                {t.menu.dayLabelsShort.map((dl, day) => {
                  const on = w.days.includes(day);
                  return (
                    <Chip
                      key={day}
                      text={dl}
                      outline={!on}
                      style={{
                        cursor: 'pointer',
                        ...(on ? { backgroundColor: 'var(--f7-theme-color)', color: '#fff' } : {}),
                      }}
                      onClick={() => toggleDay(wi, day)}
                    />
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {t.menu.from}{' '}
                  <ChipInput
                    type="time"
                    value={w.startTime}
                    onChange={(v) => setTime(wi, 'startTime', v)}
                  />
                </label>
                <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {t.menu.to}{' '}
                  <ChipInput
                    type="time"
                    value={w.endTime}
                    onChange={(v) => setTime(wi, 'endTime', v)}
                  />
                </label>
              </div>
            </Block>
          </div>
        ))}
      {scheduled && (
        <Block>
          <Button onClick={addWindow}>{t.menu.addWindow}</Button>
          <p style={{ color: 'var(--f7-block-footer-text-color)', fontSize: 13, marginTop: 8 }}>
            {t.menu.scheduleNote}
          </p>
        </Block>
      )}
    </>
  );
}
